import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Cpu, Download, Plus, Truck, CheckCircle2, Package, Shield, Anchor, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";

/**
 * /app/director/hardware — Fleet Flasher · Hardware Provisioning
 *
 * Director-only console that:
 *  • Mints hardware units bound to tech operators (Wayfinder ID auto-derived)
 *  • Downloads signed Provisioning Manifest (JSON bundle for the offline
 *    ISO-bake pipeline — NOT a multi-GB ISO file itself)
 *  • Tracks 4 lifecycle states: ordered → armored → in_transit → deployed
 */
const STATUSES = [
    { id: "ordered",    label: "Ordered",     icon: Package,    accent: "hsl(45 85% 65%)" },
    { id: "armored",    label: "Armored",     icon: Shield,     accent: "hsl(195 70% 60%)" },
    { id: "in_transit", label: "In Transit",  icon: Truck,      accent: "hsl(280 60% 65%)" },
    { id: "deployed",   label: "Deployed",    icon: CheckCircle2, accent: "hsl(151 60% 55%)" },
];
const NEXT_STATUS = { ordered: "armored", armored: "in_transit", in_transit: "deployed", deployed: null };

export default function DirectorHardware() {
    const [units, setUnits] = useState([]);
    const [counts, setCounts] = useState({ ordered: 0, armored: 0, in_transit: 0, deployed: 0 });
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [techs, setTechs] = useState([]);
    const [form, setForm] = useState({ tech_user_id: "", model: "Sovereign Sentry Pico", notes: "" });
    const [creating, setCreating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const url = filter === "all" ? "/director/hardware/units" : `/director/hardware/units?status=${filter}`;
            const { data } = await api.get(url);
            setUnits(data?.units || []);
            setCounts(data?.counts || { ordered: 0, armored: 0, in_transit: 0, deployed: 0 });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not pull hardware manifest.");
        } finally {
            setLoading(false);
        }
    };

    const loadTechs = async () => {
        // Pull operators eligible to bind a Sentry: field operatives,
        // privateers, commanders, admirals, directors.
        try {
            const { data } = await api.get("/director/overview");
            const eligible = (data?.partners || []).filter((p) =>
                ["field_operative", "founding_privateer", "td_commander", "sports_admiral", "director", "partner"].includes(p.role)
            );
            setTechs(eligible);
        } catch { /* silent — form gracefully shows empty */ }
    };

    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);
    useEffect(() => { loadTechs(); }, []);

    const create = async () => {
        if (!form.tech_user_id) {
            toast.error("Bind a tech operator first.");
            return;
        }
        setCreating(true);
        try {
            const { data } = await api.post("/director/hardware/units", form);
            toast.success(`Unit minted · Wayfinder ${data.unit?.wayfinder_id}`);
            setShowCreate(false);
            setForm({ tech_user_id: "", model: "Sovereign Sentry Pico", notes: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Mint failed.");
        } finally {
            setCreating(false);
        }
    };

    const transition = async (unitId, status, wayfinderId) => {
        try {
            await api.patch(`/director/hardware/units/${unitId}/status`, { status });
            toast.success(`${wayfinderId} → ${status}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Transition failed.");
        }
    };

    const downloadManifest = async (unitId, wayfinderId) => {
        try {
            const { data } = await api.get(`/director/hardware/units/${unitId}/provisioning-manifest`);
            // Trigger client-side download.
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${wayfinderId}-provisioning-manifest.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            const sealedSha = data?.sovereign_seal?.sha256;
            toast.success(
                `Manifest sealed · ${wayfinderId}`,
                {
                    description: sealedSha
                        ? `sha256: ${sealedSha.slice(0, 24)}…  · verify at /api/sovereign/verify`
                        : "downloaded",
                    duration: 6000,
                }
            );
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Manifest download failed.");
        }
    };

    const downloadScript = async (unitId, wayfinderId) => {
        try {
            // Browser-driven download of the sealed bash script. Uses fetch
            // (instead of api.get) so we can preserve the binary blob shape.
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/scripts/sentry-bootstrap/${unitId}`;
            const plank = JSON.parse(sessionStorage.getItem("eigoo_boarding_plank") || "{}");
            const r = await fetch(url, {
                credentials: "include",
                headers: plank.email ? {
                    "X-Director-Plank-Email": plank.email,
                    "X-Director-Plank-Password": plank.password,
                } : {},
            });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const sealedSha = r.headers.get("x-sovereign-sha256");
            const blob = await r.blob();
            const dl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = dl;
            a.download = `${wayfinderId}-bootstrap.sh`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(dl);
            toast.success(
                `Script sealed · ${wayfinderId}`,
                {
                    description: sealedSha
                        ? `sha256: ${sealedSha.slice(0, 24)}…  · banner in script header`
                        : "downloaded",
                    duration: 6000,
                }
            );
        } catch (e) {
            toast.error(e.message || "Script download failed.");
        }
    };

    const totalUnits = useMemo(() => Object.values(counts).reduce((s, n) => s + n, 0), [counts]);

    return (
        <div className="space-y-8" data-testid="director-hardware-page">
            {/* Header */}
            <div className="fade-up flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.35em] text-muted-foreground">The Fleet Flasher</div>
                    <h1 className="font-heading text-4xl sm:text-5xl mt-1 tracking-tighter">Hardware Provisioning</h1>
                    <p className="text-muted-foreground mt-2 max-w-2xl">
                        Bind Sentry units to operators. Mint signed Provisioning Manifests for the offline ISO-bake pipeline.
                        Track every unit through the 4-stage hardware covenant.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="hardware-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_13%)] transition-colors"
                    >
                        <RefreshCw size={13} /> Reload
                    </button>
                    <button
                        onClick={() => setShowCreate(true)}
                        data-testid="hardware-mint"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] transition-colors"
                    >
                        <Plus size={13} /> Mint Unit
                    </button>
                </div>
            </div>

            {/* Lifecycle stat strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 fade-up">
                <StatCard label="Total Fleet" value={totalUnits} accent="hsl(40 61% 53%)" testid="hw-stat-total" />
                {STATUSES.map((s) => (
                    <StatCard
                        key={s.id}
                        label={s.label}
                        value={counts[s.id] || 0}
                        accent={s.accent}
                        icon={s.icon}
                        testid={`hw-stat-${s.id}`}
                    />
                ))}
            </div>

            {/* Filter pills */}
            <div className="glass rounded-2xl p-4 fade-up flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Sentry Manifest · Filter</div>
                <div className="inline-flex rounded-full border border-[hsl(40_40%_30%/0.3)] p-0.5 bg-[hsl(151_55%_10%)] text-[10px] uppercase tracking-wider">
                    {[{ id: "all", label: "all", count: totalUnits }, ...STATUSES.map((s) => ({ id: s.id, label: s.id, count: counts[s.id] || 0 }))].map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            data-testid={`hw-filter-${f.id}`}
                            className={`px-3 py-1.5 rounded-full transition-colors ${filter === f.id ? "bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            {f.label.replace(/_/g, " ")} <span className="opacity-70 ml-0.5">({f.count})</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Unit list */}
            <div className="glass rounded-2xl p-6 fade-up">
                {loading ? (
                    <div className="space-y-3 animate-pulse">
                        {[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-md bg-[hsl(151_55%_13%)]" />)}
                    </div>
                ) : units.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center">
                        No units in <span className="text-foreground">{filter}</span>. Mint your first Sentry to begin provisioning.
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="hardware-list">
                        {units.map((u) => (
                            <UnitRow key={u.id} unit={u} onTransition={transition} onDownload={downloadManifest} onDownloadScript={downloadScript} />
                        ))}
                    </div>
                )}
            </div>

            {/* Operations note */}
            <div className="rounded-xl border border-[hsl(195_70%_55%/0.3)] bg-[hsl(195_50%_8%/0.5)] p-4 text-xs text-[hsl(195_50%_80%)] fade-up">
                <strong className="text-[hsl(195_85%_75%)]">Manifest workflow:</strong> the JSON bundle you download is the
                <em> input</em> to your offline ISO-bake pipeline (Packer / Yocto / cloud-init). The Sentry firmware reads the
                bootstrap_token on first boot, exchanges it for a session token via <code className="font-mono px-1.5 py-0.5 rounded bg-[hsl(195_55%_15%)]">/api/sentries/checkin</code>,
                and then begins reporting. Token expires in 24h — re-download the manifest if expired before flash.
            </div>

            {/* Mint modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="hw-mint-modal">
                    <div className="glass rounded-2xl p-6 max-w-md w-full m-4 border border-[hsl(40_50%_35%/0.4)]">
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-heading text-xl">Mint Hardware Unit</div>
                            <button onClick={() => setShowCreate(false)} aria-label="Close" className="p-1 rounded-md hover:bg-[hsl(151_55%_15%)]">
                                <X size={16} />
                            </button>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-1.5">Bind to operator</label>
                                <select
                                    value={form.tech_user_id}
                                    onChange={(e) => setForm({ ...form, tech_user_id: e.target.value })}
                                    data-testid="hw-mint-tech"
                                    className="w-full px-3 py-2 rounded-md bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.4)] text-sm focus:outline-none focus:border-[hsl(45_85%_62%)]"
                                >
                                    <option value="">— select operator —</option>
                                    {techs.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name || t.email} · {t.role}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-1.5">Model</label>
                                <input
                                    type="text"
                                    value={form.model}
                                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                                    data-testid="hw-mint-model"
                                    className="w-full px-3 py-2 rounded-md bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.4)] text-sm focus:outline-none focus:border-[hsl(45_85%_62%)]"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground block mb-1.5">Notes (optional)</label>
                                <textarea
                                    rows={2}
                                    value={form.notes}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    data-testid="hw-mint-notes"
                                    className="w-full px-3 py-2 rounded-md bg-[hsl(151_55%_8%)] border border-[hsl(40_40%_30%/0.4)] text-sm focus:outline-none focus:border-[hsl(45_85%_62%)] resize-none"
                                />
                            </div>
                        </div>
                        <div className="mt-5 flex items-center justify-end gap-2">
                            <button onClick={() => setShowCreate(false)} className="px-3 py-2 rounded-md border border-[hsl(40_40%_30%/0.4)] text-xs uppercase tracking-wider hover:bg-[hsl(151_55%_13%)]">Cancel</button>
                            <button
                                onClick={create}
                                disabled={creating || !form.tech_user_id}
                                data-testid="hw-mint-submit"
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold text-xs uppercase tracking-wider hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                            >
                                <Anchor size={13} /> {creating ? "Minting…" : "Mint Unit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, accent, icon: Icon, testid }) {
    return (
        <div className="glass rounded-xl p-4" data-testid={testid}>
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon size={12} style={{ color: accent }} />}
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            </div>
            <div className="font-heading text-3xl" style={{ color: accent }}>{value}</div>
        </div>
    );
}

function UnitRow({ unit, onTransition, onDownload, onDownloadScript }) {
    const cur = STATUSES.find((s) => s.id === unit.status) || STATUSES[0];
    const Icon = cur.icon;
    const next = NEXT_STATUS[unit.status];
    return (
        <article
            className="rounded-xl border border-[hsl(40_40%_30%/0.3)] bg-[hsl(151_55%_10%/0.6)] p-4 hover:border-[hsl(40_50%_45%/0.6)] transition-colors"
            data-testid={`hw-row-${unit.wayfinder_id}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ background: cur.accent + "22", color: cur.accent }}>
                        <Cpu size={18} strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-heading text-base font-mono text-[hsl(45_85%_72%)]" data-testid={`hw-wayfinder-${unit.wayfinder_id}`}>
                                {unit.wayfinder_id}
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider" style={{ background: cur.accent + "22", color: cur.accent }}>
                                <Icon size={10} className="inline mr-1 -mt-px" />
                                {cur.label}
                            </span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            {unit.model} · bound to <span className="text-foreground">{unit.tech_name || unit.tech_email}</span> ({unit.tech?.role || "—"})
                        </div>
                        {unit.notes && <div className="text-[11px] text-muted-foreground/80 mt-1 italic">{unit.notes}</div>}
                        {/* Lifecycle pipeline */}
                        <div className="mt-2 flex items-center gap-1 text-[9px] uppercase tracking-wider">
                            {STATUSES.map((s, idx) => {
                                const reached = STATUSES.findIndex((x) => x.id === unit.status) >= idx;
                                const isCurrent = s.id === unit.status;
                                return (
                                    <React.Fragment key={s.id}>
                                        <span
                                            className={`px-1.5 py-0.5 rounded transition-colors ${isCurrent ? "font-bold" : reached ? "opacity-90" : "opacity-30"}`}
                                            style={{ color: reached ? s.accent : "hsl(40 20% 50%)" }}
                                        >
                                            {s.label.replace(/_/g, " ")}
                                        </span>
                                        {idx < STATUSES.length - 1 && <span className="opacity-30">→</span>}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button
                        onClick={() => onDownload(unit.id, unit.wayfinder_id)}
                        data-testid={`hw-download-${unit.wayfinder_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[hsl(195_60%_25%)] text-[hsl(195_70%_85%)] text-xs uppercase tracking-wider font-semibold hover:bg-[hsl(195_65%_32%)] transition-colors"
                        title="Download Provisioning Manifest (sealed JSON for offline ISO bake)"
                    >
                        <Download size={13} /> Manifest
                    </button>
                    <button
                        onClick={() => onDownloadScript(unit.id, unit.wayfinder_id)}
                        data-testid={`hw-download-script-${unit.wayfinder_id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[hsl(45_50%_25%)] text-[hsl(45_85%_85%)] text-xs uppercase tracking-wider font-semibold hover:bg-[hsl(45_55%_32%)] transition-colors"
                        title="Download Sealed First-Boot Script (.sh with sovereign seal banner in header)"
                    >
                        <Download size={13} /> Script
                    </button>
                    {next && (
                        <button
                            onClick={() => onTransition(unit.id, next, unit.wayfinder_id)}
                            data-testid={`hw-advance-${unit.wayfinder_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[hsl(151_60%_25%)] text-[hsl(151_30%_95%)] text-xs uppercase tracking-wider font-semibold hover:bg-[hsl(151_65%_32%)] transition-colors"
                        >
                            → {next.replace(/_/g, " ")}
                        </button>
                    )}
                </div>
            </div>
        </article>
    );
}
